import EscPosEncoder from 'esc-pos-encoder-ionic';

/**
 * Builds the bytes of an image print job: the receipt of an order, or the
 * test image of a test print.
 *
 * The image goes out as `GS v 0` raster commands. Each one starts with a
 * header that says how many bytes of image follow, and the printer takes that
 * many bytes as pixels whatever they are. If a job breaks off part-way — the
 * link drops, the phone dies, the app is killed — the printer is left waiting
 * for the rest. It then eats the next receipt as pixels, prints what is left
 * of it as junk characters, and neither receipt is cut.
 *
 * `@gonnaorder/capacitor-printer` starts every job with
 * {@link PLUGIN_RESYNC_BYTES} NUL bytes that finish a broken image, but only
 * an image of at most {@link MAX_RASTER_BAND_BYTES}. So the image never goes
 * out as one block. It goes out in bands that size or smaller, each under 256
 * bytes wide and under 256 rows high, so both high bytes of every band header
 * are zero. A header
 * broken off part-way is then completed by the zeros into the header that was
 * meant, or into an empty one. `ResyncTest` in the plugin, and
 * `esc-pos-job.spec.ts` here, cut a job off at every byte to prove it.
 *
 * Before bands, a receipt was one `GS v 0` of tens of kilobytes. Broken off
 * near its start, it left more owed than any padding could finish.
 */

const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;
const CR = 0x0d;

/**
 * The largest band, in image bytes.
 *
 * Must not be larger than `EscPos.MAX_RASTER_BAND_BYTES` in the plugin, which
 * sizes the padding from it. The two live in different repositories, so the
 * build checks them against the plugin file it installs:
 * `verifyPrinterPluginBands` in capacitor-build-utils.js, run by every GoAdmin
 * build and by PR CI. It reads this line, so keep it a plain number.
 */
export const MAX_RASTER_BAND_BYTES = 2048;

/**
 * How many NUL bytes the plugin writes at the start of every job:
 * `EscPos.RESYNC` since plugin 1.0.1. The app does not send them. The number is
 * here so `esc-pos-job.spec.ts` models the plugin that ships, and the same
 * build check fails if it no longer matches the plugin file. A band and its
 * 8-byte header must fit inside it.
 */
export const PLUGIN_RESYNC_BYTES = 2112;

/** Under 256, so the high byte of a band's height is always zero. */
const MAX_BAND_ROWS = 255;

/**
 * Under 256, so the high byte of a band's width is always zero. 255 bytes is
 * 2040 dots, far wider than any receipt printer.
 */
const MAX_WIDTH_BYTES = 255;

/**
 * What the registration form's default paper width, 78.5 mm, has always drawn
 * to: 78.5 × 7 is 549.5, rounded down to a multiple of 8.
 */
const DEFAULT_HEAD_DOTS = 544;

/**
 * How wide to draw the image, in printer dots.
 *
 * Seven dots per millimetre of paper, as the app has always drawn it, but
 * never wider than the print head for that paper: 384 dots on 58 mm paper,
 * 576 on 80 mm, 832 on 112 mm. 58 × 7 is 406 — 22 dots wider than a 58 mm
 * head. A row wider than the head is cut off or wrapped, depending on the
 * model, and a wrapped image is garbage.
 *
 * A width between 80 and 112 mm gets the 80 mm head. Drawn too narrow, a
 * receipt is only narrower; drawn too wide, it is garbage.
 *
 * A missing or nonsense width gets what the form's default has always given.
 */
export function headDots(paperWidthMm: number | null | undefined): number {
  const mm = Number(paperWidthMm);
  if (!Number.isFinite(mm) || mm <= 0) {
    return DEFAULT_HEAD_DOTS;
  }
  const head = mm <= 60 ? 384 : mm < 100 ? 576 : 832;
  return Math.max(8, Math.min(head, 8 * Math.floor((mm * 7) / 8)));
}

/**
 * Rows in one band of an image `widthBytes` wide.
 *
 * As many as fit in {@link MAX_RASTER_BAND_BYTES}, under 256, and a multiple
 * of 8 — the unit the encoder draws in. `widthBytes` is at most 255, so that is
 * always at least 8.
 */
export function rowsPerBand(widthBytes: number): number {
  const rows = Math.min(
    MAX_BAND_ROWS,
    Math.floor(MAX_RASTER_BAND_BYTES / widthBytes),
  );
  return rows - (rows % 8);
}

/**
 * Cuts one `GS v 0` image, as the encoder writes it, into bands.
 *
 * The pixels are not touched: the bands, one after the other, print exactly
 * the image the single command would have.
 */
export function rasterBands(raster: Uint8Array): Uint8Array[] {
  if (
    raster.length < 8 ||
    raster[0] !== GS ||
    raster[1] !== 0x76 ||
    raster[2] !== 0x30
  ) {
    throw new Error('Expected one GS v 0 image.');
  }
  const mode = raster[3];
  const widthBytes = raster[4] | (raster[5] << 8);
  const rows = raster[6] | (raster[7] << 8);
  if (widthBytes < 1 || widthBytes > MAX_WIDTH_BYTES) {
    throw new Error(`An image ${widthBytes} bytes wide cannot be banded.`);
  }
  if (raster.length !== 8 + widthBytes * rows) {
    throw new Error(
      `A ${widthBytes}x${rows} image needs ${widthBytes * rows} bytes, ` +
        `not ${raster.length - 8}.`,
    );
  }

  const bandRows = rowsPerBand(widthBytes);
  const bands: Uint8Array[] = [];
  for (let top = 0; top < rows; top += bandRows) {
    const height = Math.min(bandRows, rows - top);
    const band = new Uint8Array(8 + widthBytes * height);
    band.set([GS, 0x76, 0x30, mode, widthBytes, 0, height, 0]);
    band.set(
      raster.subarray(8 + top * widthBytes, 8 + (top + height) * widthBytes),
      8,
    );
    bands.push(band);
  }
  return bands;
}

/**
 * A whole image job around an encoded image: reset, three lines of feed, the
 * image in bands, six lines of feed. The same layout the app has always sent,
 * with the one block cut into bands.
 *
 * No cut: the plugin adds the end of the receipt, because only it can ask the
 * printer whether it has a cutter.
 */
export function receiptJob(raster: Uint8Array): Uint8Array {
  return concat([
    new Uint8Array([ESC, 0x40]),
    feed(3),
    ...rasterBands(raster),
    feed(6),
  ]);
}

/**
 * Draws `image` to the printer's width and builds the job to print it.
 *
 * Throws if the image has no size — a broken invoice image must fail the job,
 * not print an empty receipt.
 */
export function imageJob(
  image: CanvasImageSource & { width: number; height: number },
  paperWidthMm: number | null | undefined,
): Uint8Array {
  const aspectRatio = image.height / image.width;
  if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) {
    throw new Error(
      `The image to print has no size (${image.width}x${image.height}).`,
    );
  }
  const width = headDots(paperWidthMm);
  const height = Math.max(8, 8 * Math.floor((aspectRatio * width) / 8));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('No 2D canvas to draw the image on.');
  }
  context.drawImage(image, 0, 0, width, height);

  const raster: Uint8Array = new EscPosEncoder()
    .image(canvas, width, height, 'atkinson', 1)
    .encode();
  return receiptJob(raster);
}

/** `lines` newlines, each LF CR as the encoder writes them. */
function feed(lines: number): Uint8Array {
  const bytes = new Uint8Array(lines * 2);
  for (let i = 0; i < lines; i++) {
    bytes[2 * i] = LF;
    bytes[2 * i + 1] = CR;
  }
  return bytes;
}

function concat(parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, part) => n + part.length, 0));
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}
